import { fold, hasTerm } from "./eventfit";

/**
 * Personal interests — the rooms she would go to anyway.
 *
 * The keyword pass in `eventfit` marks a run club or a yoga class down for
 * everyone, and it is right to: those rooms are not a founder's credibility.
 * But a founder who runs meets people at a founders run that she would never
 * meet at a pitch night, so the penalty is wrong *for her*. This file is the
 * per-founder answer: her onboarding picks (the `personal` question in
 * `INTEREST_QUESTIONS`) matched against an event's title at read time.
 *
 * Three things keep it honest:
 *
 *  - **Nothing here touches the global score.** `keywordFit`'s verdict is a
 *    property of the room; this is a property of the match, and it lives in
 *    the read path only. The card keeps showing the global number and adds a
 *    referral line that names its source.
 *  - **One vocabulary for chips, matcher and card.** The chip she tapped, the
 *    terms that match a title and the "because you run" line are one row, so
 *    they cannot drift apart.
 *  - **Terms are data.** Tuning what counts as a running event is editing a
 *    row, never a branch (rule #3 in spirit).
 *
 * Deliberate exclusions from the term lists, all measured against real
 * titles: no "summit" under hiking (fires on "AI Summit"), no "dinner" under
 * food (fires on investor dinners), no bare "club" anywhere (book club and
 * run club are phrases; "club" alone fires on nightlife and member clubs).
 */

/** The question id in `profiles.interests` that holds these picks. */
export const PERSONAL_QUESTION_ID = "personal";

export interface PersonalInterest {
  id: string;
  /** The chip text in onboarding; doubles as the label in discovery context. */
  label: string;
  /** The card's referral line, in the register of a friend who knows her. */
  why: string;
  /** Title vocabulary. Matched by `eventfit`'s whole-word/phrase rules —
   *  activity nouns, because an organiser names an event after the activity,
   *  not the category ("Golden Gate 10K", never "Running"). */
  terms: string[];
}

export const PERSONAL_INTERESTS: PersonalInterest[] = [
  {
    id: "running",
    label: "Running",
    why: "because you run",
    terms: ["run", "runs", "running", "run club", "5k", "10k", "marathon", "track night"],
  },
  {
    id: "hiking",
    label: "Hiking and trails",
    why: "because you hike",
    terms: ["hike", "hiking", "trail", "trails", "backpacking"],
  },
  {
    id: "wellness",
    label: "Yoga and wellness",
    why: "because you make time for wellness",
    terms: ["yoga", "wellness", "meditation", "sound bath", "breathwork", "pilates"],
  },
  {
    id: "food_coffee",
    label: "Food and coffee",
    why: "because you chase good coffee",
    terms: ["coffee", "brunch", "supper club", "tasting", "cooking", "food festival"],
  },
  {
    id: "music",
    label: "Live music",
    why: "because you go where the music is",
    terms: ["live music", "concert", "dj", "vinyl", "jazz", "open mic", "karaoke"],
  },
  {
    id: "art_film",
    label: "Art and film",
    why: "because you make time for art",
    terms: ["gallery", "art walk", "museum", "screening", "film", "exhibit"],
  },
  {
    id: "sports",
    label: "Pickup sports",
    why: "because you play",
    terms: [
      "pickleball",
      "tennis",
      "soccer",
      "basketball",
      "volleyball",
      "climbing",
      "bouldering",
      "cycling",
    ],
  },
  {
    id: "books",
    label: "Books and writing",
    why: "because you read",
    // No bare "reading" — it fires on "Annual Report Reading" and every
    // town called Reading. The activity nouns below only name book rooms.
    terms: ["book club", "poetry", "writers", "writing", "author talk", "literary"],
  },
];

/**
 * How far a personal match lifts a room in *her* feed ordering.
 *
 * Sized against the keyword vocabulary it corrects for: enough to carry a
 * penalised-to-zero founders run past the weak word matches ("founders" is
 * 10, "seed" is 20), never enough to pass a room with a real work signal
 * ("pitch" is 35, "investor" is 40). A friend mentioning it, not a takeover.
 * The lift is a sort key only — the displayed score never changes.
 */
export const PERSONAL_LIFT = 25;

/**
 * Her first pick whose vocabulary appears in the title, or null.
 *
 * Title only, for the same reason `eventfit`'s penalty is title-only: a
 * conference whose description mentions a morning run is still a conference,
 * and the symmetric claim holds — it is not a running event either.
 *
 * Picks are tried in her order: the three-pick cap makes the answer a
 * ranking, so her first interest decides which referral line the card shows
 * when two match. An id this file no longer knows (an old pick after the
 * options change) matches nothing rather than throwing — a stale answer must
 * never crash the feed.
 */
export function personalMatch(
  pickedIds: readonly string[] | null | undefined,
  title: string,
): { id: string; label: string; why: string } | null {
  if (!pickedIds || pickedIds.length === 0) return null;
  const folded = fold(title);
  for (const pick of pickedIds) {
    const interest = PERSONAL_INTERESTS.find((i) => i.id === pick);
    if (!interest) continue;
    if (interest.terms.some((term) => hasTerm(folded, term))) {
      return { id: interest.id, label: interest.label, why: interest.why };
    }
  }
  return null;
}
